import { createReducer } from 'redux-starter-kit';
import { selectRequest } from '../../domain/requests/RequestReducer';
import {
  fetchCreditPackages, fetchPurchasePackage, setChosenCreditPackage, fetchBraintreeToken,
  fetchPurchaseHistory, fetchPurchaseHistoryAsAdmin, fetchUnconfirmedPurchases, searchPurchases, fetchUpdatePurchase, fetchFailPurchase, fetchAddFreeCredits,
} from './purchaseCreditsUiActions';
import { selectCreditPackages, selectCreditPackage } from '../../domain/credits/creditPackageReducer';


/**
 * A selector for the current package request state.
 * @param {object} state The redux state of the application.
 * @returns The request object.
 * @example const request = selectCreditPackagesRequest(state);
 */
export const selectCreditPackagesRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestCreditPackagesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the current purchaseHistory request state.
 * @param {object} state The redux state of the application.
 * @returns The request object.
 * @example const request = selectPurchaseHistoryRequest(state);
 */
export const selectPurchaseHistoryRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestPurchaseHistoryId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the currently loaded packages for the Purchase Credits page.
 * @param {object} state The redux state of the application.
 * @returns An array of credit packages. Null if the credit packages have not yet loaded.
 * @example const creditPackages = selectLoadedCreditPackages(state);
 */
export const selectLoadedCreditPackages = (state) => {
  const request = selectRequest(state, fetchCreditPackages.toString());
  if (!request || request.isLoading) return null;

  return selectCreditPackages(state);
};

/**
 * A selector for the current chosen package.
 * @param {object} state The redux state of the application.
 * @returns The credit package. (Null if nothing chosen)
 * @example const chosenCreditPackage = selectChosenCreditPackage(state);
 */
export const selectChosenCreditPackage = (state) => {
  const id = state.via.ui.purchaseCredits.selectedPackageId;
  if (!id) return null;

  return selectCreditPackage(state, id);
};

/**
 * A selector for the current purchase package request state.
 * @param {object} state The redux state of the application.
 */
export const selectPurchasePackageRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestPurchasePackageId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectPurchaseHistoryAsAdminRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestPurchaseHistoryAsAdminId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectUnconfirmedPurchasesRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestUnconfirmedPurchasesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectPurchaseSearchRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestSearchPurchasesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectUpdatePurchaseRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestUpdatePurchaseRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectFailPurchaseRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestFailPurchaseRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectAddFreeCreditsRequest = (state) => {
  const requestId = state.via.ui.purchaseCredits.requestAddFreeCreditsRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};


const creditsInitialState = {
  requestCreditPackagesId: null,
  selectedPackageId: null,
  requestPurchasePackageId: null,
  requestPurchaseHistoryAsAdminId: null,
  requestUnconfirmedPurchasesId: null,
  requestUpdatePurchaseRequestId: null,
  requestFailPurchaseRequestId: null,
  requestSearchPurchasesId: null,
  requestAddFreeCreditsRequestId: null,
};

/**
 * The reducer for the Purchase Credits page.
 */
export default createReducer(creditsInitialState, {
  // On page entry, fetch all the credit packages.
  [fetchCreditPackages]: (state, action) => ({
    ...creditsInitialState, // Reset the state. This is always the first action.
    requestCreditPackagesId: action.payload,
  }),

  // On user selection of a credit package.
  [setChosenCreditPackage]: (state, action) => ({
    ...state,
    selectedPackageId: action.payload,
    requestPurchasePackageId: null, // Reset the past purchase indicator.
  }),

  // A new token is needed for each transaction.
  [fetchBraintreeToken]: (state, action) => ({
    ...state,
    requestBraintreeTokenId: action.payload,
  }),

  // On user purchase of a credit package.
  [fetchPurchasePackage]: (state, action) => ({
    ...state,
    requestPurchasePackageId: action.payload,
  }),

  [fetchPurchaseHistory]: (state, action) => ({
    ...state,
    requestPurchaseHistoryId: action.payload,
  }),

  // ADMIN ACTIONS

  [fetchPurchaseHistoryAsAdmin]: (state, action) => ({
    ...state,
    requestPurchaseHistoryAsAdminId: action.payload,
  }),

  [fetchUnconfirmedPurchases]: (state, action) => ({
    ...state,
    requestUnconfirmedPurchasesId: action.payload,
  }),

  [searchPurchases]: (state, action) => ({
    ...state,
    requestSearchPurchasesId: action.payload,
  }),

  [fetchUpdatePurchase]: (state, action) => ({
    ...state,
    requestUpdatePurchaseRequestId: action.payload,
  }),

  [fetchFailPurchase]: (state, action) => ({
    ...state,
    requestFailPurchaseRequestId: action.payload,
  }),

  [fetchAddFreeCredits]: (state, action) => ({
    ...state,
    requestAddFreeCreditsRequestId: action.payload,
  }),
});
